/**
 * Module #36 — Reports builder: pure spec ⇆ builder-state logic.
 *
 * Extracted from AdminReports.tsx so the column-ORDER behaviour is unit-tested
 * without a DOM. The selected columns are an ORDERED `string[]` (array order =
 * the report's output column order, which the IE compiler emits as the SELECT
 * list, so on-screen / run-table / CSV all share one order). No React here.
 */

import type {
  Aggregation,
  FilterOp,
  ReportDatasetMeta,
  ReportSpec,
  SpecFilter,
} from '../../hooks/useReports';

export const MONEY_TYPES = new Set(['cents']);
export const NUMERIC_TYPES = new Set(['int', 'cents', 'bps', 'ratio']);

export function isNumericType(t: string): boolean {
  return NUMERIC_TYPES.has(t);
}

export function safeString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return JSON.stringify(value) ?? '';
}

export interface FilterRow {
  field: string;
  op: FilterOp;
  value: string;
}

/**
 * Builder state → ReportSpec. `included` is the ordered column list; its order
 * becomes spec.columns order (and thus the output/CSV column order).
 */
export function buildSpec(
  dataset: ReportDatasetMeta,
  included: string[],
  summarize: boolean,
  grouped: Set<string>,
  aggs: Record<string, Aggregation>,
  filterRows: FilterRow[],
): ReportSpec {
  const fieldByKey = new Map(dataset.fields.map((f) => [f.key, f]));
  const grouping = summarize && grouped.size > 0;

  let columns: ReportSpec['columns'];
  let groupBy: string[] | undefined;
  if (grouping) {
    // GROUP BY follows the visible column order (grouped ⊆ included).
    groupBy = included.filter((key) => grouped.has(key));
    columns = included
      .map((key): ReportSpec['columns'][number] | null => {
        const f = fieldByKey.get(key);
        if (f === undefined) return null;
        if (grouped.has(key)) return { field: key };
        // Default to the field's FIRST allowed aggregation — not a hardcoded
        // 'sum', which the compiler rejects for measures like Reserve/Estimate
        // whose aggregations exclude sum (and which the agg <select> never offers).
        if (f.measure) return { field: key, agg: aggs[key] ?? f.aggregations?.[0] ?? 'sum' };
        return null; // non-grouped dimension can't appear in a grouped query
      })
      .filter((c): c is ReportSpec['columns'][number] => c !== null);
  } else {
    columns = included.map((key) => ({ field: key }));
  }

  const filters: SpecFilter[] = [];
  for (const row of filterRows) {
    if (row.field.length === 0) continue;
    const f = fieldByKey.get(row.field);
    const numeric = f !== undefined && isNumericType(f.type);
    const op = row.op;
    if (op === 'is_null' || op === 'is_not_null') {
      filters.push({ field: row.field, op });
    } else if (op === 'in') {
      const parts = row.value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (parts.length > 0) {
        filters.push({ field: row.field, op, value: numeric ? parts.map(Number) : parts });
      }
    } else if (op === 'between') {
      const parts = row.value.split(',').map((s) => s.trim());
      if (parts.length === 2) {
        filters.push({
          field: row.field,
          op,
          value: numeric ? [Number(parts[0]), Number(parts[1])] : parts,
        });
      }
    } else if (op === 'contains') {
      if (row.value.length > 0) filters.push({ field: row.field, op, value: row.value });
    } else {
      if (row.value.length > 0) {
        filters.push({ field: row.field, op, value: numeric ? Number(row.value) : row.value });
      }
    }
  }

  return {
    dataset: dataset.key,
    columns,
    ...(groupBy !== undefined && groupBy.length > 0 && { groupBy }),
    ...(filters.length > 0 && { filters }),
  };
}

// ── Preset spec → builder state (reverse of buildSpec) ──────────────────────

export interface BuilderState {
  included: string[];
  summarize: boolean;
  grouped: Set<string>;
  aggs: Record<string, Aggregation>;
  filterRows: FilterRow[];
}

export function filterValueToString(f: SpecFilter): string {
  if (f.op === 'is_null' || f.op === 'is_not_null' || f.value === undefined) return '';
  if (Array.isArray(f.value)) return f.value.map((v) => safeString(v)).join(', ');
  return safeString(f.value);
}

export function specToBuilderState(spec: ReportSpec): BuilderState {
  // Preserve the preset's authored column ORDER verbatim.
  const included = spec.columns.map((c) => c.field);
  const grouped = new Set(spec.groupBy ?? []);
  const aggs: Record<string, Aggregation> = {};
  for (const c of spec.columns) if (c.agg !== undefined) aggs[c.field] = c.agg;
  const filterRows: FilterRow[] = (spec.filters ?? []).map((f) => ({
    field: f.field,
    op: f.op,
    value: filterValueToString(f),
  }));
  return { included, summarize: grouped.size > 0, grouped, aggs, filterRows };
}

// ── Ordered-list helpers (pure; the array order IS the report column order) ──

/** Move the item at `from` to index `to`. Out-of-range → returned unchanged. */
export function reorder(arr: string[], from: number, to: number): string[] {
  if (from < 0 || from >= arr.length || to < 0 || to >= arr.length || from === to) return arr;
  const next = arr.slice();
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return arr;
  next.splice(to, 0, moved);
  return next;
}

/**
 * Translate a drag drop "insert before the target row" into the move-to index
 * that `reorder` expects. Dragging DOWN removes the source first, shifting the
 * target left by one — so the destination is `target - 1`; dragging UP keeps it.
 */
export function dropIndex(from: number, target: number): number {
  return from < target ? target - 1 : target;
}

/** Append a column key, refusing duplicates (compiler also guards). */
export function withColumnAdded(arr: string[], key: string): string[] {
  return arr.includes(key) ? arr : [...arr, key];
}

export function withColumnRemoved(arr: string[], key: string): string[] {
  return arr.filter((k) => k !== key);
}

/** Return a copy of `rec` without `key` (used to prune aggs when a column is removed). */
export function omitKey<T>(rec: Record<string, T>, key: string): Record<string, T> {
  if (!(key in rec)) return rec;
  const rest: Record<string, T> = {};
  for (const [k, v] of Object.entries(rec)) {
    if (k !== key) rest[k] = v;
  }
  return rest;
}
